import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import {
  InfiniteData,
  keepPreviousData,
  skipToken,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  GetFinalReviewSamplesApiV1FlywheelDatasetIdFinalReviewFlowIdLabelGetQueryParams,
  fetchBulkSubmitSampleReviewApiV1FlywheelDatasetIdReviewFlowIdPut,
  fetchGetFinalReviewSamplesApiV1FlywheelDatasetIdFinalReviewFlowIdLabelGet,
  useGetFinalReviewSamplesApiV1FlywheelDatasetIdFinalReviewFlowIdLabelGet,
  useGetFinalReviewSummaryApiV1FlywheelDatasetIdFinalReviewFlowIdGet,
} from "../../vl-api/vlComponents";
import {
  FinalReviewLabelSummary,
  FinalReviewSample,
  FinalReviewSamplesPage,
  FinalReviewSummary,
  FinalReviewTab,
  SortOrder,
} from "../../vl-api/vlSchemas";
import {
  DATA_PAGE_PARAMETER,
  FINAL_REVIEW_UNLABELED,
  FINAL_REVIEW_VALUE,
  LABEL_CLASS_PARAMETER,
  REVIEW_PARAMETER,
  REVIEW_TAB_PARAMETER,
  VERTEX_CONTEXT,
  VERTEX_ID,
  VERTEX_SIMILARITY,
  VERTEX_TYPE,
} from "../../helpers/constants/miscellaneous";
import {
  setNavigationCluster,
  setSimilarityVertex,
  setTemporaryExportData,
} from "../../redux/SingleDataset/actions";
import VLToastComponent from "../../views/components/VLToastComponent";
import { CounterItem } from "../../views/components/LabelPropagation/Counters";
import { useFlywheelStatus } from "./useFlywheelStatus";

export type FinalReviewTabLabel = "All" | "VL" | "Changed";

export const FINAL_REVIEW_TABS: FinalReviewTabLabel[] = [
  "All",
  "VL",
  "Changed",
];

const TAB_TO_API: Record<FinalReviewTabLabel, FinalReviewTab> = {
  All: "all",
  VL: "vl",
  Changed: "changed",
};

export const FINAL_REVIEW_PAGE_SIZE = 50;

export const DEFAULT_FINAL_REVIEW_SORT: SortOrder = "asc";

export const isSortOrder = (value: string | null): value is SortOrder =>
  value === "asc" || value === "desc";

// TODO: drop once the API client is regenerated with sort_order (VL-8790)
type FinalReviewSamplesQueryParams =
  GetFinalReviewSamplesApiV1FlywheelDatasetIdFinalReviewFlowIdLabelGetQueryParams & {
    sort_order?: SortOrder;
  };

const SUMMARY_OPERATION_ID =
  "getFinalReviewSummaryApiV1FlywheelDatasetIdFinalReviewFlowIdGet";
const SAMPLES_OPERATION_ID =
  "getFinalReviewSamplesApiV1FlywheelDatasetIdFinalReviewFlowIdLabelGet";

export const isFinalReviewTab = (
  value: string | null,
): value is FinalReviewTabLabel =>
  !!value && FINAL_REVIEW_TABS.includes(value as FinalReviewTabLabel);

export const isFinalReviewOpen = (searchParams: URLSearchParams) =>
  searchParams.get(REVIEW_PARAMETER) === FINAL_REVIEW_VALUE;

/** Net change of a label in the final review: images moved in minus images moved out or ignored */
export const getLabelDelta = (label: FinalReviewLabelSummary) =>
  (label.moved_in_count ?? 0) -
  (label.moved_out_count ?? 0) -
  (label.ignored_count ?? 0);

/** Total images in a label for the given tab, used to paginate the samples */
export const getLabelTabCount = (
  label: FinalReviewLabelSummary | undefined,
  tab: FinalReviewTabLabel,
  isUnlabeled: boolean,
) => {
  if (!label) return 0;
  const total = label.total_count ?? 0;
  if (isUnlabeled) {
    // every ignored image was changed in the final review, none is a VL label
    return tab === "VL" ? 0 : total;
  }
  switch (tab) {
    case "VL":
      return label.vl_count ?? 0;
    case "Changed":
      return label.moved_in_count ?? 0;
    default:
      return total;
  }
};

export const findSummaryLabel = (
  summary: FinalReviewSummary | undefined,
  labelId: string | null,
) => {
  if (!summary || !labelId) return undefined;
  if (labelId === FINAL_REVIEW_UNLABELED) return summary.unlabeled;
  return summary.labels.find((l) => l.label_category_id === labelId);
};

export const getFinalReviewCounterItems = (
  summary: FinalReviewSummary | undefined,
): CounterItem[] => {
  if (!summary) return [];
  return [
    ...summary.labels.map((label) => ({
      id: label.label_category_id ?? label.label,
      label: label.label,
      count: label.total_count ?? 0,
      delta: getLabelDelta(label),
    })),
    {
      id: FINAL_REVIEW_UNLABELED,
      label: summary.unlabeled.label || "Unlabeled",
      count: summary.unlabeled.total_count ?? 0,
      dashed: true,
      tooltip:
        "Images marked as Ignore. They will not be labeled when you apply.",
    },
  ];
};

export const useFinalReviewSummary = (
  datasetId?: string,
  flowId?: string | null,
) => {
  const flywheelStatus = useFlywheelStatus(datasetId);
  // the endpoint answers 409 unless the flow is in FINAL_REVIEW
  const enabled = !!datasetId && !!flowId && flywheelStatus === "FINAL_REVIEW";
  return useGetFinalReviewSummaryApiV1FlywheelDatasetIdFinalReviewFlowIdGet(
    enabled
      ? {
          pathParams: { datasetId: datasetId!, flowId: flowId! },
          queryParams: { num_previews: 0 },
        }
      : skipToken,
    { placeholderData: keepPreviousData },
  );
};

export const useFinalReviewSamples = ({
  datasetId,
  flowId,
  labelId,
  tab,
  sortOrder,
  page,
}: {
  datasetId?: string;
  flowId?: string | null;
  labelId?: string | null;
  tab: FinalReviewTabLabel;
  sortOrder: SortOrder;
  page: number;
}) => {
  const flywheelStatus = useFlywheelStatus(datasetId);
  const enabled =
    !!datasetId && !!flowId && !!labelId && flywheelStatus === "FINAL_REVIEW";
  return useGetFinalReviewSamplesApiV1FlywheelDatasetIdFinalReviewFlowIdLabelGet(
    enabled
      ? {
          pathParams: {
            datasetId: datasetId!,
            flowId: flowId!,
            label: labelId!,
          },
          queryParams: {
            tab: TAB_TO_API[tab],
            sort_order: sortOrder,
            page,
            page_size: FINAL_REVIEW_PAGE_SIZE,
          } as FinalReviewSamplesQueryParams,
        }
      : skipToken,
    // no placeholder data: while another label / tab / page loads, show loaders, not the previous samples
  );
};

/**
 * The samples of a label from startPage on, loaded page by page (for the review modal's carousel). Shares the
 * samples query key prefix, so final review edits update and refetch it like the page query.
 */
export const useFinalReviewSamplesFrom = ({
  datasetId,
  flowId,
  labelId,
  tab,
  sortOrder,
  startPage,
  initialPage,
  enabled = true,
}: {
  datasetId?: string;
  flowId?: string | null;
  labelId?: string | null;
  tab: FinalReviewTabLabel;
  sortOrder: SortOrder;
  startPage: number;
  /** the already loaded startPage, shown while the query loads */
  initialPage?: FinalReviewSamplesPage;
  enabled?: boolean;
}) => {
  const flywheelStatus = useFlywheelStatus(datasetId);
  return useInfiniteQuery({
    queryKey: [
      SAMPLES_OPERATION_ID,
      "from-page",
      datasetId,
      flowId,
      labelId,
      tab,
      sortOrder,
      startPage,
    ],
    queryFn: ({ pageParam, signal }) =>
      fetchGetFinalReviewSamplesApiV1FlywheelDatasetIdFinalReviewFlowIdLabelGet(
        {
          pathParams: {
            datasetId: datasetId!,
            flowId: flowId!,
            label: labelId!,
          },
          queryParams: {
            tab: TAB_TO_API[tab],
            sort_order: sortOrder,
            page: pageParam,
            page_size: FINAL_REVIEW_PAGE_SIZE,
          } as FinalReviewSamplesQueryParams,
        },
        signal,
      ),
    initialPageParam: startPage,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.page + 1 : undefined,
    placeholderData: initialPage
      ? { pages: [initialPage], pageParams: [startPage] }
      : undefined,
    enabled:
      enabled &&
      !!datasetId &&
      !!flowId &&
      !!labelId &&
      flywheelStatus === "FINAL_REVIEW",
  });
};

const withoutSamples = (page: FinalReviewSamplesPage, sampleIds: string[]) => ({
  ...page,
  samples: page.samples.filter((s) => !sampleIds.includes(s.id)),
});

const isInfiniteData = (
  data: unknown,
): data is InfiniteData<FinalReviewSamplesPage> =>
  !!data && Array.isArray((data as InfiniteData<unknown>).pages);

export const useInvalidateFinalReview = () => {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [SUMMARY_OPERATION_ID] }),
        // drop cached pages of other labels / tabs instead of showing outdated samples when revisited
        queryClient.removeQueries({
          queryKey: [SAMPLES_OPERATION_ID],
          type: "inactive",
        }),
        queryClient.invalidateQueries({ queryKey: [SAMPLES_OPERATION_ID] }),
      ]),
    [queryClient],
  );
};

type FinalReviewEdit = {
  reviewResult: "REASSIGN" | "IGNORE" | "UNDO";
  labelCategoryId?: string;
  sampleIds: string[];
};

/**
 * The edits that bring samples back to the state they had before the last action. UNDO restores the state from
 * before the final review, so it only fits samples that were not changed yet; changed ones are moved back.
 */
export const getRestoreEdits = (
  samples: FinalReviewSample[],
): FinalReviewEdit[] => {
  const edits = new Map<string, FinalReviewEdit>();
  samples.forEach((sample) => {
    const edit: Omit<FinalReviewEdit, "sampleIds"> =
      !sample.changed_in_final_review
        ? { reviewResult: "UNDO" }
        : sample.ignored
          ? { reviewResult: "IGNORE" }
          : {
              reviewResult: "REASSIGN",
              labelCategoryId: sample.label_category_id,
            };
    const key = `${edit.reviewResult}:${edit.labelCategoryId ?? ""}`;
    const group = edits.get(key) ?? { ...edit, sampleIds: [] };
    group.sampleIds.push(sample.id);
    edits.set(key, group);
  });
  return Array.from(edits.values());
};

const showActionFailedToast = () =>
  toast(
    <VLToastComponent
      title="Review Action Failed"
      status="ERROR"
      description="Something went wrong. Please try again later."
    />,
  );

/**
 * Reassign / ignore samples in the final review, with an Undo toast. Uses the cycle review endpoints, which switch
 * to final review edits while the flow is in FINAL_REVIEW. Reassigning to the Unlabeled class is an ignore.
 */
export const useFinalReviewEdit = (
  datasetId?: string,
  flowId?: string | null,
) => {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateFinalReview();

  // an edited sample always leaves the label it is shown in: hide it right away,
  // the refetch afterwards syncs the list (and restores the sample if the edit failed)
  const removeSamples = useCallback(
    (sampleIds: string[]) =>
      queryClient.setQueriesData<
        FinalReviewSamplesPage | InfiniteData<FinalReviewSamplesPage>
      >({ queryKey: [SAMPLES_OPERATION_ID] }, (data) => {
        if (!data) return data;
        if (isInfiniteData(data)) {
          return {
            ...data,
            pages: data.pages.map((page) => withoutSamples(page, sampleIds)),
          };
        }
        return withoutSamples(data, sampleIds);
      }),
    [queryClient],
  );

  /** Sends the edits, returns whether they all succeeded */
  const submit = useCallback(
    async (edits: FinalReviewEdit[]) => {
      if (!datasetId || !flowId) return false;
      try {
        for (const edit of edits) {
          if (!edit.sampleIds.length) continue;
          await fetchBulkSubmitSampleReviewApiV1FlywheelDatasetIdReviewFlowIdPut(
            {
              pathParams: { datasetId, flowId },
              queryParams: {
                review_result: edit.reviewResult,
                label_category_id: edit.labelCategoryId,
              },
              body: { sample_ids: edit.sampleIds },
            },
          );
        }
        return true;
      } catch (err) {
        console.error("Final review action failed", err);
        showActionFailedToast();
        return false;
      } finally {
        await invalidate();
      }
    },
    [datasetId, flowId, invalidate],
  );

  const undo = useCallback(
    (samples: FinalReviewSample[]) => submit(getRestoreEdits(samples)),
    [submit],
  );

  const editWithUndo = useCallback(
    async (
      samples: FinalReviewSample[],
      edit: Omit<FinalReviewEdit, "sampleIds">,
      title: string,
      targetName: string,
    ) => {
      if (!samples.length) return;
      removeSamples(samples.map((s) => s.id));
      const succeeded = await submit([
        { ...edit, sampleIds: samples.map((s) => s.id) },
      ]);
      if (!succeeded) return;

      const isBulk = samples.length > 1;
      const toastId = toast(
        <VLToastComponent
          title={title}
          status="SUCCESS"
          description={`${isBulk ? `${samples.length} images` : "Image"} moved to ${targetName}`}
          actionButton={{
            text: "Undo",
            type: "primary",
            onClick: () => {
              toast.dismiss(toastId);
              undo(samples);
            },
          }}
          onClose={() => toast.dismiss(toastId)}
        />,
        { autoClose: 7000, closeButton: false, closeOnClick: false },
      );
    },
    [removeSamples, submit, undo],
  );

  const ignore = useCallback(
    (samples: FinalReviewSample[]) =>
      editWithUndo(samples, { reviewResult: "IGNORE" }, "Ignore", "Unlabeled"),
    [editWithUndo],
  );

  const reassign = useCallback(
    (samples: FinalReviewSample[], labelId: string, labelName: string) =>
      labelId === FINAL_REVIEW_UNLABELED
        ? ignore(samples)
        : editWithUndo(
            samples,
            { reviewResult: "REASSIGN", labelCategoryId: labelId },
            "Reassign",
            labelName,
          ),
    [editWithUndo, ignore],
  );

  return { reassign, ignore, undo };
};

/** Navigate to the final review page with the given label (defaults to the first label) */
export const useOpenFinalReview = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  return useCallback(
    (datasetId: string, labelId: string) => {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set(REVIEW_PARAMETER, FINAL_REVIEW_VALUE);
      searchParams.set(LABEL_CLASS_PARAMETER, labelId);
      searchParams.set(REVIEW_TAB_PARAMETER, "All");
      searchParams.delete(DATA_PAGE_PARAMETER);
      [VERTEX_SIMILARITY, VERTEX_CONTEXT, VERTEX_ID, VERTEX_TYPE].forEach((p) =>
        searchParams.delete(p),
      );
      dispatch(setSimilarityVertex(null));
      dispatch(setNavigationCluster(null));
      dispatch(setTemporaryExportData(null));
      navigate({
        pathname: `/dataset/${datasetId}/data`,
        search: searchParams.toString(),
      });
    },
    [dispatch, navigate],
  );
};
